import type { DynamicFeeBracket } from "@investhinks/api-client";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";

import DynamicFeeEditor from "../../../../components/shared/DynamicFeeEditor";

interface TierEditModalProps {
  isOpen: boolean;
  toggle: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editingTier: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tierForm: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFormChange: (e: any) => void;
  onSave: () => void;
  saving: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSetDefault: (tier: any) => void;
}

const TierEditModal: React.FC<TierEditModalProps> = ({
  isOpen,
  toggle,
  editingTier,
  tierForm,
  onFormChange,
  onSave,
  saving,
  onSetDefault,
}) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "settings" });
  const [activeTab, setActiveTab] = useState("general");
  const [setDefaultHover, setSetDefaultHover] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const handleClose = () => {
    setActiveTab("general");
    setConfirmModalOpen(false);
    toggle();
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleClose} size="lg">
        <ModalHeader toggle={handleClose}>
          {editingTier ? t("tierModal.editTitle") : t("tierModal.createTitle")}
        </ModalHeader>
        <ModalBody>
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={activeTab === "general" ? "active" : ""}
                onClick={() => setActiveTab("general")}
                style={{ cursor: "pointer" }}
              >
                <i className="fas fa-cog mr-2"></i>
                {t("tierModal.tabGeneral")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "pricing" ? "active" : ""}
                onClick={() => setActiveTab("pricing")}
                style={{ cursor: "pointer" }}
              >
                <i className="fas fa-percentage mr-2"></i>
                {t("tierModal.tabPricing")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "invoiceTexts" ? "active" : ""}
                onClick={() => setActiveTab("invoiceTexts")}
                style={{ cursor: "pointer" }}
              >
                <i className="fas fa-file-invoice mr-2"></i>
                {t("tierModal.tabInvoiceTexts")}
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="general">
              <Form>
                <FormGroup>
                  <Label for="tierName" className="form-control-label">
                    {t("tierModal.name")} *
                  </Label>
                  <Input
                    type="text"
                    id="tierName"
                    name="name"
                    value={tierForm.name}
                    onChange={onFormChange}
                    placeholder={t("tierModal.namePlaceholder")}
                    disabled={saving}
                  />
                  {editingTier && (
                    <small className="form-text text-muted">
                      {t("tierModal.code")}: <code>{editingTier.code}</code>
                    </small>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label for="tierDescription" className="form-control-label">
                    {t("tierModal.description")}
                  </Label>
                  <Input
                    type="textarea"
                    id="tierDescription"
                    name="description"
                    value={tierForm.description}
                    onChange={onFormChange}
                    placeholder={t("tierModal.descriptionPlaceholder")}
                    disabled={saving}
                    rows={2}
                  />
                </FormGroup>

                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="tierMonthlyPrice" className="form-control-label">
                        {t("tierModal.monthlyPrice")} *
                      </Label>
                      <Input
                        type="number"
                        id="tierMonthlyPrice"
                        name="monthlyPrice"
                        min="0"
                        step="0.01"
                        value={tierForm.monthlyPrice}
                        onChange={onFormChange}
                        disabled={saving}
                      />
                      <small className="form-text text-muted">{t("tierModal.monthlyPriceHelp")}</small>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="tierVpsOverride" className="form-control-label">
                        {t("tierModal.vpsOverride")} *
                      </Label>
                      <Input
                        type="number"
                        id="tierVpsOverride"
                        name="vpsOverride"
                        min="0"
                        step="0.01"
                        value={tierForm.vpsOverride}
                        onChange={onFormChange}
                        disabled={saving}
                        required
                      />
                      <small className="form-text text-muted">{t("tierModal.vpsOverrideHelp")}</small>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="tierSortOrder" className="form-control-label">
                        {t("tierModal.sortOrder")}
                      </Label>
                      <Input
                        type="number"
                        id="tierSortOrder"
                        name="sortOrder"
                        min="0"
                        value={tierForm.sortOrder}
                        onChange={onFormChange}
                        disabled={saving}
                      />
                      <small className="form-text text-muted">{t("tierModal.sortOrderHelp")}</small>
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup check className="mt-3">
                  <Label check>
                    <Input
                      type="checkbox"
                      name="isActive"
                      checked={tierForm.isActive}
                      onChange={onFormChange}
                      disabled={saving || (editingTier?.isDefault && tierForm.isActive)}
                    />{" "}
                    {t("tierModal.isActive")}
                    {editingTier?.isDefault && tierForm.isActive && (
                      <small className="text-muted ml-2">({t("tiers.cannotDisableDefault")})</small>
                    )}
                  </Label>
                </FormGroup>
              </Form>
            </TabPane>

            <TabPane tabId="pricing">
              <div className="mb-3">
                <small className="text-muted">{t("tierModal.pricingHelp")}</small>
              </div>

              <FormGroup>
                <DynamicFeeEditor
                  value={tierForm.dynamicFee}
                  onChange={(newFees: DynamicFeeBracket[]) =>
                    onFormChange({
                      target: { name: "dynamicFee", value: newFees },
                    })
                  }
                  disabled={saving}
                />
              </FormGroup>

              <FormGroup check className="mt-4">
                <Label check>
                  <Input
                    type="checkbox"
                    name="commissionOnlyBilling"
                    checked={tierForm.commissionOnlyBilling || false}
                    onChange={onFormChange}
                    disabled={saving}
                  />{" "}
                  {t("tierModal.commissionOnlyBilling")}
                </Label>
                <small className="form-text text-muted d-block">{t("tierModal.commissionOnlyBillingHelp")}</small>
              </FormGroup>
            </TabPane>

            <TabPane tabId="invoiceTexts">
              <div className="mb-3">
                <small className="text-muted">{t("tierModal.invoiceTextsHelp")}</small>
              </div>

              <Form>
                <FormGroup>
                  <Label for="tierVpsText" className="form-control-label">
                    {t("tierModal.vpsText")}
                  </Label>
                  <Input
                    type="text"
                    id="tierVpsText"
                    name="vpsText"
                    value={tierForm.vpsText}
                    onChange={onFormChange}
                    placeholder={t("tierModal.vpsTextPlaceholder")}
                    disabled={saving}
                  />
                  <small className="form-text text-muted">{t("tierModal.vpsTextHelp")}</small>
                </FormGroup>

                <FormGroup>
                  <Label for="tierTierText" className="form-control-label">
                    {t("tierModal.tierText")}
                  </Label>
                  <Input
                    type="text"
                    id="tierTierText"
                    name="tierText"
                    value={tierForm.tierText}
                    onChange={onFormChange}
                    placeholder={t("tierModal.tierTextPlaceholder")}
                    disabled={saving}
                  />
                  <small className="form-text text-muted">{t("tierModal.tierTextHelp")}</small>
                </FormGroup>

                <FormGroup>
                  <Label for="tierProfitFeeText" className="form-control-label">
                    {t("tierModal.profitFeeText")}
                  </Label>
                  <Input
                    type="text"
                    id="tierProfitFeeText"
                    name="profitFeeText"
                    value={tierForm.profitFeeText}
                    onChange={onFormChange}
                    placeholder={t("tierModal.profitFeeTextPlaceholder")}
                    disabled={saving}
                  />
                  <small className="form-text text-muted">{t("tierModal.profitFeeTextHelp")}</small>
                </FormGroup>

                <FormGroup>
                  <Label for="tierDiscountText" className="form-control-label">
                    {t("tierModal.discountText")}
                  </Label>
                  <Input
                    type="text"
                    id="tierDiscountText"
                    name="discountText"
                    value={tierForm.discountText}
                    onChange={onFormChange}
                    placeholder={t("tierModal.discountTextPlaceholder")}
                    disabled={saving}
                  />
                  <small className="form-text text-muted">{t("tierModal.discountTextHelp")}</small>
                </FormGroup>
              </Form>
            </TabPane>
          </TabContent>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <div>
            {editingTier && !editingTier.isDefault && (
              <Button
                color="warning"
                style={{
                  border: "1px solid #f5a623",
                  backgroundColor: setDefaultHover ? "#f5a623" : "#fff8e6",
                  color: setDefaultHover ? "#fff" : "#f5a623",
                }}
                onMouseEnter={() => setSetDefaultHover(true)}
                onMouseLeave={() => setSetDefaultHover(false)}
                onClick={() => setConfirmModalOpen(true)}
                disabled={saving}
              >
                <i className="fas fa-star mr-2"></i>
                {t("tiers.setAsDefault")}
              </Button>
            )}
          </div>
          <div>
            <Button color="secondary" onClick={handleClose} disabled={saving} className="mr-2">
              {t("tierModal.cancel")}
            </Button>
            <Button color="primary" onClick={onSave} disabled={saving}>
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  {t("tierModal.saving")}
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  {editingTier ? t("tierModal.update") : t("tierModal.create")}
                </>
              )}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Set Default Confirmation Modal */}
      <Modal isOpen={confirmModalOpen && isOpen} toggle={() => setConfirmModalOpen(false)} centered>
        <ModalHeader toggle={() => setConfirmModalOpen(false)}>
          <i className="fas fa-star text-warning mr-2"></i>
          {t("tiers.setAsDefault")}
        </ModalHeader>
        <ModalBody>
          <p>{t("tiers.setDefaultConfirm", { name: editingTier?.name })}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModalOpen(false)}>
            {t("tierModal.cancel")}
          </Button>
          <Button
            color="warning"
            onClick={() => {
              if (editingTier) {
                onSetDefault(editingTier);
              }
              setConfirmModalOpen(false);
              handleClose();
            }}
          >
            <i className="fas fa-star mr-2"></i>
            {t("tiers.setAsDefault")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TierEditModal;
